// src/pages/chat/CTA/cta.hooks.tsx
import * as React from "react";
import type { Phase } from "./cta";
import { ENVIRONMENT } from "../../../env";
import { pickMimeType } from "../../../utils/audio";
import { requestWakeLock, releaseWakeLock } from "../../../utils/wake-lock";

const log = (...args: any[]) => {
    if (ENVIRONMENT === "DEV") console.log("[cta.hooks.tsx]", ...args);
};

type Videos = {
    pauseOff: string;
    pauseOn: string;
    recordingLoop: string;
    restToRecording: string;
    restLoop: string;
    stopRecordingFromPause: string;
    stopRecordingFromRecording: string;
};

type Args = {
    disabled: boolean;
    defaultRecording: boolean;
    videos: Videos;

    onStartRecording?: () => void;
    onStopRecording?: () => void;
    onReturnToRest?: () => void;
    onPhaseChange?: (phase: Phase) => void;

    // ✅ NEW: send audio + transcript on stop
    onSendRecording?: (audioBlob: Blob, transcript?: string) => void;

    // ✅ Error callback when recording fails (e.g. no audio captured)
    onRecordingFailed?: (message: string) => void;
};

function safePlay(v: HTMLVideoElement) {
    const p = v.play();
    if (p && typeof (p as any).catch === "function") (p as any).catch(() => {});
}

function waitCanPlay(v: HTMLVideoElement) {
    if (v.readyState >= 3) return Promise.resolve();
    return new Promise<void>((resolve) => {
        const onReady = () => {
            v.removeEventListener("canplay", onReady);
            resolve();
        };
        v.addEventListener("canplay", onReady, { once: true } as any);
    });
}

// allowNearEnd=false is important for short transition clips (pauseOn/pauseOff/stop clips)
function onceEndedOrTimeout(
    v: HTMLVideoElement,
    fallbackMs = 1400,
    opts?: { allowNearEnd?: boolean },
) {
    const allowNearEnd = opts?.allowNearEnd ?? true;

    const durationMs = Number.isFinite(v.duration) && v.duration > 0
        ? Math.ceil(v.duration * 1000 + 250)
        : fallbackMs;

    return new Promise<void>((resolve) => {
        let done = false;

        const cleanup = () => {
            v.removeEventListener("ended", onEnded);
            v.removeEventListener("timeupdate", onTime);
            v.removeEventListener("error", onErr);
            if (t) window.clearTimeout(t);
        };

        const finish = (reason: string) => {
            if (done) return;
            done = true;
            cleanup();
            if (ENVIRONMENT === "DEV") {
                log("onceEndedOrTimeout resolve:", reason, {
                    src: v.currentSrc,
                    t: v.currentTime,
                    d: v.duration,
                });
            }
            resolve();
        };

        const onEnded = () => finish("ended");
        const onErr = () => finish("error");

        const onTime = () => {
            if (!allowNearEnd) return;
            if (
                Number.isFinite(v.duration) && v.duration > 0 &&
                v.currentTime >= v.duration - 0.06
            ) {
                finish("timeupdate-near-end");
            }
        };

        v.addEventListener("ended", onEnded, { once: true } as any);
        v.addEventListener("error", onErr, { once: true } as any);
        v.addEventListener("timeupdate", onTime);

        const t = window.setTimeout(() => finish("timeout"), durationMs);

        if (v.ended) finish("already-ended");
    });
}

function hardResetToStart(v: HTMLVideoElement) {
    try {
        v.pause();
    } catch {}
    try {
        v.currentTime = 0;
    } catch {}
}

// ✅ Freeze without seeking (seeking is what was causing “pauseOn/off not playing properly”)
function freezeAtEnd(v: HTMLVideoElement) {
    try {
        v.pause();
    } catch {}
    // do NOT set currentTime here; keep whatever frame the browser rendered
}

// ✅ allow the last frame to actually paint before pausing
function nextPaint() {
    return new Promise<void>((resolve) => {
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
}

/* =========================
   ✅ Audio recording helpers (MediaRecorder)
   ========================= */

export function useRecordingCtaButton({
    disabled,
    defaultRecording,
    videos,
    onStartRecording,
    onStopRecording,
    onReturnToRest,
    onPhaseChange,
    onSendRecording,
    onRecordingFailed,
}: Args) {
    const v0 = React.useRef<HTMLVideoElement>(null);
    const v1 = React.useRef<HTMLVideoElement>(null);

    // Ref for stable access in long-lived closures (onerror, track.onended)
    const onRecordingFailedRef = React.useRef(onRecordingFailed);
    onRecordingFailedRef.current = onRecordingFailed;

    const [activeIndex, setActiveIndex] = React.useState<0 | 1>(0);
    const activeIndexRef = React.useRef<0 | 1>(0);

    const [phase, setPhase] = React.useState<Phase>("restLoop");
    const phaseRef = React.useRef<Phase>("restLoop");

    const [isBusyTransition, setIsBusyTransition] = React.useState(false);

    const flowIdRef = React.useRef(0);
    const didInitRef = React.useRef(false);

    const isRecording = phase === "toRecording" ||
        phase === "recording" ||
        phase === "toPaused" ||
        phase === "paused" ||
        phase === "toResume";

    const isPaused = phase === "paused" || phase === "toResume";

    const setPhaseSafe = React.useCallback(
        (p: Phase) => {
            phaseRef.current = p;
            setPhase(p);
            onPhaseChange?.(p);
            log("setPhase ->", p);
        },
        [onPhaseChange],
    );

    const getVideo = (idx: 0 | 1) => (idx === 0 ? v0.current : v1.current);
    const inactiveIndex =
        () => ((activeIndexRef.current === 0 ? 1 : 0) as 0 | 1);

    const swapActive = React.useCallback((idx: 0 | 1) => {
        activeIndexRef.current = idx;
        setActiveIndex(idx);
        log("swapActive ->", idx);
    }, []);

    const setSrcLoopAndLoad = React.useCallback(
        async (v: HTMLVideoElement, src: string, loop: boolean) => {
            const srcChanged = v.getAttribute("data-src") !== src;

            if (srcChanged) {
                v.setAttribute("data-src", src);
                v.src = src;
                log("set src ->", src);
            }

            v.loop = loop;

            if (srcChanged || v.readyState < 2) {
                // New src or not buffered yet — full reload needed
                hardResetToStart(v);
                v.load();
                await waitCanPlay(v);
            } else {
                // Same src already buffered — seek to start instantly, no reload
                try { v.pause(); } catch {}
            }

            try {
                v.currentTime = 0;
            } catch {}
        },
        [],
    );

    const interruptTo = React.useCallback(
        async (src: string, loop: boolean) => {
            const nextIdx = inactiveIndex();
            const nextV = getVideo(nextIdx);
            const curV = getVideo(activeIndexRef.current);
            if (!nextV || !curV) return;

            log("interruptTo:", { src, loop });

            // Load the new video BEHIND the old one (old stays visible)
            await setSrcLoopAndLoad(nextV, src, loop);
            safePlay(nextV);

            // Wait for the new video to decode its first frame so the
            // crossfade never shows a blank/black frame
            await new Promise<void>((resolve) => {
                if (nextV.readyState >= 2) {
                    resolve();
                    return;
                }
                const onFrame = () => {
                    nextV.removeEventListener("playing", onFrame);
                    resolve();
                };
                nextV.addEventListener("playing", onFrame, { once: true });
                // Fallback — don't hang forever
                setTimeout(resolve, 100);
            });

            // Now swap: new fades in, old fades out (CSS 180ms crossfade)
            swapActive(nextIdx);

            // Pause the old video AFTER the crossfade completes so both
            // videos overlap and blend during the morph transition
            setTimeout(() => {
                try { curV.pause(); } catch {}
            }, 500);

            return nextV;
        },
        [setSrcLoopAndLoad, swapActive],
    );

    // =========================
    // ✅ Audio recording state (MediaRecorder)
    // =========================
    const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
    const audioChunksRef = React.useRef<Blob[]>([]);
    const streamRef = React.useRef<MediaStream | null>(null);
    const mimeTypeRef = React.useRef<string>("");
    const isMicReadyRef = React.useRef(false);
    const pausedByVisibilityRef = React.useRef(false);
    const wakeLockRef = React.useRef<WakeLockSentinel | null>(null);

    const cleanupStream = React.useCallback(() => {
        try {
            if (mediaRecorderRef.current) {
                mediaRecorderRef.current.ondataavailable = null;
                mediaRecorderRef.current.onstop = null;
                mediaRecorderRef.current.onerror = null;
            }
        } catch {}
        try {
            streamRef.current?.getTracks()?.forEach((t) => t.stop());
        } catch {}
        streamRef.current = null;
        mediaRecorderRef.current = null;
        isMicReadyRef.current = false;
        audioChunksRef.current = [];
        releaseWakeLock(wakeLockRef.current);
        wakeLockRef.current = null;
    }, []);

    const ensureRecorder = React.useCallback(async () => {
        if (isMicReadyRef.current && mediaRecorderRef.current) return;

        const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
        });
        streamRef.current = stream;

        const mimeType = pickMimeType();
        mimeTypeRef.current = mimeType;

        audioChunksRef.current = [];

        let mr: MediaRecorder;
        try {
            if (mimeType) {
                mr = new MediaRecorder(
                    stream,
                    { mimeType, audioBitsPerSecond: 128000 } as any,
                );
            } else {
                mr = new MediaRecorder(stream);
            }
        } catch {
            mr = new MediaRecorder(stream);
        }

        mr.ondataavailable = (e) => {
            if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
        };

        // Layer 2: MediaRecorder.onerror — detect OS kill while backgrounded
        // Use ref to avoid stale closure (handler persists across re-renders)
        mr.onerror = () => {
            onRecordingFailedRef.current?.("Recording was interrupted. Your audio up to this point has been saved.");
            cleanupStream();
        };

        // Layer 3: track.onended — detect mic disconnection (Bluetooth, OS revoke)
        stream.getTracks().forEach((track) => {
            track.onended = () => {
                if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
                    onRecordingFailedRef.current?.("Microphone was disconnected. Saving your recording...");
                    cleanupStream();
                }
            };
        });

        mediaRecorderRef.current = mr;
        isMicReadyRef.current = true;

        if (ENVIRONMENT === "DEV") {
            log("MediaRecorder ready:", {
                mimeType,
                state: mr.state,
                trackCount: stream.getTracks().length,
            });
        }
    }, [cleanupStream]);

    const startAudioRecording = React.useCallback(async () => {
        try {
            await ensureRecorder();
            const mr = mediaRecorderRef.current;
            if (!mr) return;

            if (mr.state === "paused") {
                mr.resume();
                // Re-acquire wake lock after pause
                requestWakeLock().then((lock) => { wakeLockRef.current = lock; });
                return;
            }

            if (mr.state === "recording") return;

            mr.start(1000);
            // Keep screen awake while recording
            requestWakeLock().then((lock) => { wakeLockRef.current = lock; });
            if (ENVIRONMENT === "DEV") log("MediaRecorder start ->", mr.state);
        } catch (e) {
            log("startAudioRecording failed:", e);
            cleanupStream();
        }
    }, [ensureRecorder, cleanupStream]);

    const pauseAudioRecording = React.useCallback(() => {
        try {
            const mr = mediaRecorderRef.current;
            if (mr && mr.state === "recording") {
                mr.pause();
                if (ENVIRONMENT === "DEV") {
                    log("MediaRecorder pause ->", mr.state);
                }
            }
        } catch {}
    }, []);

    const resumeAudioRecording = React.useCallback(() => {
        try {
            const mr = mediaRecorderRef.current;
            if (mr && mr.state === "paused") {
                mr.resume();
                if (ENVIRONMENT === "DEV") {
                    log("MediaRecorder resume ->", mr.state);
                }
            }
        } catch {}
    }, []);

    const stopAudioRecording = React.useCallback(
        async (): Promise<Blob | null> => {
            const mr = mediaRecorderRef.current;
            if (!mr) {
                cleanupStream();
                return null;
            }

            if (
                mr.state === "inactive" && audioChunksRef.current.length === 0
            ) {
                cleanupStream();
                return null;
            }

            const blob = await new Promise<Blob | null>((resolve) => {
                let done = false;

                const finish = () => {
                    if (done) return;
                    done = true;

                    const chunks = audioChunksRef.current || [];
                    if (!chunks || chunks.length === 0) {
                        resolve(null);
                        return;
                    }

                    const finalMime = mimeTypeRef.current || chunks[0]?.type ||
                        "audio/webm";
                    resolve(new Blob(chunks, { type: finalMime }));
                };

                const prevOnStop = mr.onstop;
                mr.onstop = (ev: any) => {
                    try {
                        (prevOnStop as any)?.(ev);
                    } catch {}
                    finish();
                };

                try {
                    if (mr.state !== "inactive") mr.stop();
                    else finish();
                } catch {
                    finish();
                }

                window.setTimeout(() => finish(), 1500);
            });

            if (ENVIRONMENT === "DEV") {
                log(
                    "MediaRecorder stop -> blob:",
                    blob ? { type: blob.type, size: blob.size } : null,
                );
            }

            cleanupStream();
            return blob;
        },
        [cleanupStream],
    );

    // ✅ Preload all CTA videos into the browser's media cache on mount.
    // Uses hidden <video preload="auto"> elements — this is the only reliable
    // way to populate the media cache on Android WebView (fetch() uses a
    // separate HTTP cache that <video> elements don't read from).
    const didPreloadRef = React.useRef(false);
    React.useEffect(() => {
        if (didPreloadRef.current) return;
        didPreloadRef.current = true;

        const allSrcs = [
            videos.restLoop,
            videos.restToRecording,
            videos.recordingLoop,
            videos.pauseOn,
            videos.pauseOff,
            videos.stopRecordingFromRecording,
            videos.stopRecordingFromPause,
        ];

        const preloadEls: HTMLVideoElement[] = [];
        for (const src of allSrcs) {
            const v = document.createElement("video");
            v.preload = "auto";
            v.muted = true;
            v.playsInline = true;
            v.style.position = "fixed";
            v.style.width = "1px";
            v.style.height = "1px";
            v.style.opacity = "0";
            v.style.pointerEvents = "none";
            v.style.zIndex = "-1";
            v.src = src;
            v.load();
            document.body.appendChild(v);
            preloadEls.push(v);
        }
        // Keep preload elements alive — removing them from the DOM can
        // evict the media from the browser cache on Android WebView.
        // Clean up only on unmount.
        return () => {
            for (const v of preloadEls) {
                try { document.body.removeChild(v); } catch {}
            }
        };

        log("preloading all CTA videos via hidden elements:", allSrcs);
    }, [
        videos.restLoop,
        videos.restToRecording,
        videos.recordingLoop,
        videos.pauseOn,
        videos.pauseOff,
        videos.stopRecordingFromRecording,
        videos.stopRecordingFromPause,
    ]);

    // ✅ Initial: start in restLoop and loop until user interacts
    React.useEffect(() => {
        if (didInitRef.current) return;
        didInitRef.current = true;

        const a = v0.current;
        const b = v1.current;
        if (!a || !b) return;

        const myFlow = ++flowIdRef.current;

        (async () => {
            if (defaultRecording) {
                setPhaseSafe("recording");
                await setSrcLoopAndLoad(a, videos.recordingLoop, true);
                safePlay(a);
                window.dispatchEvent(new Event("cta:ready"));
                await setSrcLoopAndLoad(b, videos.restLoop, true);
                return;
            }

            setPhaseSafe("restLoop");
            await setSrcLoopAndLoad(a, videos.restLoop, true);
            safePlay(a);
            window.dispatchEvent(new Event("cta:ready"));

            await setSrcLoopAndLoad(b, videos.restToRecording, false);

            if (flowIdRef.current !== myFlow) return;
            log("initial restLoop running");
        })();
    }, [
        defaultRecording,
        videos.recordingLoop,
        videos.restLoop,
        videos.restToRecording,
        setPhaseSafe,
        setSrcLoopAndLoad,
    ]);

    const runStartFlowRef = React.useRef<() => Promise<void>>(async () => {});
    const runPauseFlowRef = React.useRef<() => Promise<void>>(async () => {});
    const runResumeFlowRef = React.useRef<() => Promise<void>>(async () => {});
    const runStopFlowRef = React.useRef<() => Promise<void>>(async () => {});
    const runCancelFlowRef = React.useRef<() => Promise<void>>(async () => {});
    const runReRecordFlowRef = React.useRef<() => Promise<void>>(
        async () => {},
    );

    /**
     * START:
     * restLoop -> restToRecording (wait end STRICT) -> recordingLoop (loop)
     */
    const runStartFlow = React.useCallback(async () => {
        if (disabled || isBusyTransition) return;

        const myFlow = ++flowIdRef.current;
        setIsBusyTransition(true);

        // ✅ Start visual immediately — mic setup runs in parallel with the transition animation
        setPhaseSafe("toRecording");
        const audioPromise = startAudioRecording();
        const v = await interruptTo(videos.restToRecording, false);
        if (v) await Promise.all([onceEndedOrTimeout(v, 1600, { allowNearEnd: false }), audioPromise]);

        if (flowIdRef.current !== myFlow) return;

        setPhaseSafe("recording");
        onStartRecording?.();

        await interruptTo(videos.recordingLoop, true);

        if (flowIdRef.current !== myFlow) return;
        setIsBusyTransition(false);
    }, [
        disabled,
        interruptTo,
        isBusyTransition,
        onStartRecording,
        setPhaseSafe,
        startAudioRecording,
        videos.recordingLoop,
        videos.restToRecording,
    ]);

    /**
     * PAUSE:
     * recordingLoop -> pauseOn (wait end STRICT) -> freeze last frame (NO SEEK)
     */
    const runPauseFlow = React.useCallback(async () => {
        if (disabled || isBusyTransition) return;
        if (phaseRef.current !== "recording") return;

        const myFlow = ++flowIdRef.current;
        setIsBusyTransition(true);

        pauseAudioRecording();

        setPhaseSafe("toPaused");
        const v = await interruptTo(videos.pauseOn, false);

        if (v) await onceEndedOrTimeout(v, 1400, { allowNearEnd: false });

        if (flowIdRef.current !== myFlow) return;

        await nextPaint();
        const cur = getVideo(activeIndexRef.current);
        if (cur) freezeAtEnd(cur);

        setPhaseSafe("paused");
        setIsBusyTransition(false);
    }, [
        disabled,
        interruptTo,
        isBusyTransition,
        pauseAudioRecording,
        setPhaseSafe,
        videos.pauseOn,
    ]);

    /**
     * RESUME:
     * paused -> pauseOff (wait end STRICT) -> recordingLoop (loop)
     */
    const runResumeFlow = React.useCallback(async () => {
        if (disabled || isBusyTransition) return;
        if (phaseRef.current !== "paused") return;

        const myFlow = ++flowIdRef.current;
        setIsBusyTransition(true);

        resumeAudioRecording();

        setPhaseSafe("toResume");
        const v = await interruptTo(videos.pauseOff, false);

        if (v) await onceEndedOrTimeout(v, 1400, { allowNearEnd: false });

        if (flowIdRef.current !== myFlow) return;

        setPhaseSafe("recording");
        await interruptTo(videos.recordingLoop, true);

        if (flowIdRef.current !== myFlow) return;
        setIsBusyTransition(false);
    }, [
        disabled,
        interruptTo,
        isBusyTransition,
        resumeAudioRecording,
        setPhaseSafe,
        videos.pauseOff,
        videos.recordingLoop,
    ]);

    /**
     * STOP:
     * paused -> stopFromPause (strict) -> restLoop (loop)
     * recording -> stopFromRecording (strict) -> restLoop (loop)
     *
     * ✅ Also: stop MediaRecorder immediately, transcribe, then call onSendRecording.
     */
    const stopFlowLockRef = React.useRef(false);
    const runStopFlow = React.useCallback(async () => {
        if (disabled || isBusyTransition || stopFlowLockRef.current) return;
        stopFlowLockRef.current = true;

        const p = phaseRef.current;
        const stoppingFromPaused = p === "paused" || p === "toResume" ||
            p === "toPaused";
        const stoppingFromRecording = p === "recording" || p === "toRecording";

        if (!stoppingFromPaused && !stoppingFromRecording) return;

        const myFlow = ++flowIdRef.current;
        setIsBusyTransition(true);

        onStopRecording?.();
        setPhaseSafe("toStop");

        const audioBlobPromise = stopAudioRecording();

        const stopSrc = stoppingFromPaused
            ? videos.stopRecordingFromPause
            : videos.stopRecordingFromRecording;
        const v = await interruptTo(stopSrc, false);

        if (v) await onceEndedOrTimeout(v, 1800, { allowNearEnd: false });

        if (flowIdRef.current !== myFlow) return;

        setPhaseSafe("restLoop");
        onReturnToRest?.();

        await interruptTo(videos.restLoop, true);

        // Send audio immediately — backend handles transcription asynchronously.
        try {
            const audioBlob = await audioBlobPromise;
            if (!audioBlob) {
                onRecordingFailed?.("Recording failed \u2014 no audio was captured. Please try again.");
            } else {
                onSendRecording?.(audioBlob, undefined);
            }
        } catch (err) {
            log("CTA stop/send failed:", err);
        }

        if (flowIdRef.current !== myFlow) { stopFlowLockRef.current = false; return; }
        setIsBusyTransition(false);
        stopFlowLockRef.current = false;
    }, [
        disabled,
        interruptTo,
        isBusyTransition,
        onRecordingFailed,
        onReturnToRest,
        onSendRecording,
        onStopRecording,
        onStopRecording,
        setPhaseSafe,
        stopAudioRecording,
        videos.restLoop,
        videos.stopRecordingFromPause,
        videos.stopRecordingFromRecording,
    ]);

    /**
     * CANCEL (NO SEND):
     * - stop MediaRecorder + discard chunks
     * - immediately return to restLoop (no transcription, no onSendRecording)
     */
    const runCancelFlow = React.useCallback(async () => {
        if (disabled) return;

        const p = phaseRef.current;
        const cancelFromAnything = p === "paused" ||
            p === "toResume" ||
            p === "toPaused" ||
            p === "recording" ||
            p === "toRecording" ||
            p === "toStop";

        if (!cancelFromAnything) return;

        const myFlow = ++flowIdRef.current;
        setIsBusyTransition(true);

        // ✅ Reset visuals immediately — audio teardown runs in parallel
        setPhaseSafe("restLoop");
        onReturnToRest?.();
        const stopPromise = stopAudioRecording().catch(() => {});

        await Promise.all([interruptTo(videos.restLoop, true), stopPromise]);

        if (flowIdRef.current !== myFlow) return;
        setIsBusyTransition(false);
    }, [
        disabled,
        interruptTo,
        onReturnToRest,
        setPhaseSafe,
        stopAudioRecording,
        videos.restLoop,
    ]);

    /**
     * RE-RECORD (NO SEND, STAY IN RECORDING FLOW):
     * - stop + discard audio
     * - immediately start a fresh recording
     * - drive visuals back into restToRecording -> recordingLoop
     *
     * ✅ Avoids sitting in restLoop long enough to feel like "cancel".
     */
    const runReRecordFlow = React.useCallback(async () => {
        if (disabled || isBusyTransition) return;

        const p = phaseRef.current;
        const canRerecord = p === "paused" ||
            p === "toResume" ||
            p === "toPaused" ||
            p === "recording" ||
            p === "toRecording";

        // If already at rest, just start.
        if (p === "restLoop") {
            runStartFlowRef.current?.();
            return;
        }

        if (!canRerecord) return;

        const myFlow = ++flowIdRef.current;
        setIsBusyTransition(true);

        // ✅ Start visual immediately — stop old + start new audio runs in parallel with animation
        setPhaseSafe("toRecording");
        const audioRestartPromise = stopAudioRecording()
            .catch(() => {})
            .then(() => startAudioRecording());

        const v = await interruptTo(videos.restToRecording, false);
        if (v) await Promise.all([onceEndedOrTimeout(v, 1600, { allowNearEnd: false }), audioRestartPromise]);

        if (flowIdRef.current !== myFlow) return;

        setPhaseSafe("recording");
        onStartRecording?.();

        await interruptTo(videos.recordingLoop, true);

        if (flowIdRef.current !== myFlow) return;
        setIsBusyTransition(false);
    }, [
        disabled,
        interruptTo,
        isBusyTransition,
        onStartRecording,
        setPhaseSafe,
        startAudioRecording,
        stopAudioRecording,
        videos.recordingLoop,
        videos.restToRecording,
    ]);

    React.useEffect(() => {
        runStartFlowRef.current = runStartFlow;
        runPauseFlowRef.current = runPauseFlow;
        runResumeFlowRef.current = runResumeFlow;
        runStopFlowRef.current = runStopFlow;
        runCancelFlowRef.current = runCancelFlow;
        runReRecordFlowRef.current = runReRecordFlow;
    }, [
        runStartFlow,
        runPauseFlow,
        runResumeFlow,
        runStopFlow,
        runCancelFlow,
        runReRecordFlow,
    ]);

    // Layer 1: Visibility change — auto-pause when app goes to background
    React.useEffect(() => {
        const handleVisibility = () => {
            const mr = mediaRecorderRef.current;
            if (!mr) return;

            if (document.hidden) {
                if (mr.state === "recording") {
                    mr.pause();
                    pausedByVisibilityRef.current = true;
                    log("CTA: auto-paused by visibility");
                }
            } else {
                if (pausedByVisibilityRef.current && mr.state === "paused") {
                    mr.resume();
                    pausedByVisibilityRef.current = false;
                    // Re-acquire wake lock (browser releases it when page goes hidden)
                    requestWakeLock().then((lock) => { wakeLockRef.current = lock; });
                    log("CTA: auto-resumed by visibility");
                }
            }
        };
        document.addEventListener("visibilitychange", handleVisibility);
        return () => document.removeEventListener("visibilitychange", handleVisibility);
    }, []);

    // Layer 4: beforeunload + pagehide — warn when closing tab/PWA during recording
    React.useEffect(() => {
        const warnOnLeave = (e: Event) => {
            if (isRecording) {
                e.preventDefault();
                (e as BeforeUnloadEvent).returnValue = "";
            }
        };
        window.addEventListener("beforeunload", warnOnLeave);
        window.addEventListener("pagehide", warnOnLeave);
        return () => {
            window.removeEventListener("beforeunload", warnOnLeave);
            window.removeEventListener("pagehide", warnOnLeave);
        };
    }, [isRecording]);

    /**
     * CTA CLICK RULES:
     * - restLoop -> start
     * - recording -> pause
     * - paused -> resume
     */
    const handlePress = React.useCallback(
        (e: React.PointerEvent) => {
            e.preventDefault();
            if (disabled || isBusyTransition) return;

            const p = phaseRef.current;

            if (p === "restLoop") {
                runStartFlowRef.current?.();
                return;
            }

            if (p === "recording") {
                runPauseFlowRef.current?.();
                return;
            }

            if (p === "paused") {
                runResumeFlowRef.current?.();
                return;
            }
        },
        [disabled, isBusyTransition],
    );

    // ✅ imperative controls (dock + overlay)
    const start = React.useCallback(() => {
        if (disabled || isBusyTransition) return;
        if (phaseRef.current === "restLoop") runStartFlowRef.current?.();
    }, [disabled, isBusyTransition]);

    const pause = React.useCallback(() => {
        if (disabled || isBusyTransition) return;
        if (phaseRef.current === "recording") runPauseFlowRef.current?.();
    }, [disabled, isBusyTransition]);

    const resume = React.useCallback(() => {
        if (disabled || isBusyTransition) return;
        if (phaseRef.current === "paused") runResumeFlowRef.current?.();
    }, [disabled, isBusyTransition]);

    const stop = React.useCallback(() => {
        if (disabled || isBusyTransition) return;
        runStopFlowRef.current?.();
    }, [disabled, isBusyTransition]);

    const cancel = React.useCallback(() => {
        if (disabled) return;
        runCancelFlowRef.current?.();
    }, [disabled]);

    const reRecord = React.useCallback(() => {
        if (disabled || isBusyTransition) return;
        runReRecordFlowRef.current?.();
    }, [disabled, isBusyTransition]);

    const getPhase = React.useCallback(() => phaseRef.current, []);

    return {
        isRecording,
        isPaused,
        isBusyTransition,
        handlePress,
        phase,
        activeIndex,
        v0: { ref: v0 },
        v1: { ref: v1 },
        start,
        stop,
        cancel,
        pause,
        resume,
        reRecord,
        getPhase,
    };
}
